"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppManager } from "@/context/AppManagerContext";
import { AppManager } from "@/services/AppManager";
import { OrganizationService } from "@/services/OrganizationService";
import { UsersService } from "@/services/UserService";
import { InvitesService } from "@/services/InvitesService";
import { PlansService } from "@/services/PlansService";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";

import { OrganizationsAccordion } from "./components/OrganizationsAccordion";
import { InvitesAccordion } from "./components/InvitesAccordion";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  type OrganizationsOutput,
  type UsersOutput,
  type InvitesOutput,
  UserRoles,
} from "@/interfaces";
import type { PlansOutput } from "@/interfaces/plan";
import { OrganizationsHeader } from "./components/OrganizationsHeader";

const colors = {
  background: "#D6D8CB",
  primary: "#EE7B11",
  text: "#050505",
};

export default function Organizations() {
  const [organizations, setOrganizations] = useState<OrganizationsOutput[]>([]);
  const [invites, setInvites] = useState<InvitesOutput[]>([]);
  const [plans, setPlans] = useState<PlansOutput[]>([]);
  const [user, setUser] = useState<UsersOutput>();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const { setCurrentOrganization, setCurrentRole } = useAppManager();

  const organizationService = new OrganizationService();
  const usersService = new UsersService();
  const invitesService = new InvitesService();
  const plansService = new PlansService();

  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, []);

  const fetchData = async () => {
    const fetchedUser = await usersService.getUserMe();
    const [organizationsResponse, invitesResponse, { data: plansResponse }] =
      await Promise.all([
        organizationService.getByUsers(fetchedUser.userId, {
          expand: ["users", "plan"],
        }),
        invitesService.findByUser(fetchedUser.userId, {
          expand: "organizations",
        }),
        plansService.findAll({ expand: ["features"] }),
      ]);
    setOrganizations(organizationsResponse ?? []);
    setInvites(invitesResponse ?? []);
    setPlans(plansResponse ?? []);
    setUser(fetchedUser);
  };

  const handleNavigate = (id?: string) => {
    navigate(id ? `/organizacoes/${id}/editar` : `/organizacoes/novo`);
  };

  const handleDeleteClick = async (organizationId: string) => {
    openDeleteDialog(organizationId);
    return Promise.resolve();
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await organizationService.delete(itemToDelete);
      await fetchData();
    } finally {
      setLoading(false);
      closeDeleteDialog();
    }
  };

  const getUserRoles = (organization: OrganizationsOutput): UserRoles => {
    if (!user) return UserRoles.MEMBER;
    const foundUser = organization.users?.find(
      (member) => member.user?.userId === user.userId
    );
    return foundUser?.role || UserRoles.MEMBER;
  };

  const startSession = (organization: OrganizationsOutput) => {
    setCurrentOrganization(organization);
    setCurrentRole(getUserRoles(organization));
    AppManager.getInstance().setOrganizationId(organization.id);
    navigate(`/inicio`);
  };

  const handleAcceptInvite = async (inviteId: string) => {
    try {
      setLoading(true);
      await invitesService.acceptInvite(inviteId);
      await fetchData();
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineInvite = async (inviteId: string) => {
    try {
      setLoading(true);
      await invitesService.declineInvite(inviteId);
      await fetchData();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <TooltipProvider>
      <div
        className="min-h-screen p-4"
        style={{ backgroundColor: colors.background, color: colors.text }}
      >
        <div className="container mx-auto space-y-6">
          <OrganizationsHeader onNavigate={handleNavigate} onLogout={logout} />
          <OrganizationsAccordion
            organizations={organizations}
            onHandleNavigate={handleNavigate}
            onDeleteOrganization={handleDeleteClick}
            getUserRoles={getUserRoles}
            startSession={startSession}
            plans={plans}
          />
          <InvitesAccordion
            invites={invites}
            onAcceptInvite={handleAcceptInvite}
            onDeclineInvite={handleDeclineInvite}
          />

          <DeleteConfirmationAlert
            isOpen={isOpen}
            onClose={closeDeleteDialog}
            onConfirm={handleConfirmDelete}
            title="Confirmar Exclusão"
            message="Tem certeza que deseja excluir esta organização? Todos os dados associados serão perdidos."
            itemName={
              organizations.find((org) => org.id === itemToDelete)?.name ||
              "esta organização"
            }
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
